import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Calendar, FileDown } from 'lucide-react';
import Header from '../components/Layout/Header';
import { reportData } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const FiltersCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const MetricCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MetricLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MetricValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxxl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 8px;
`;

const MetricChange = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.successText};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  margin-bottom: 24px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ViewDetailsButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
  }
`;

const ExportSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const ExportButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ExportButton = styled.button`
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const Reportes = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Header title="Reportes" />
      
      <Content>
        <Section>
          <SectionTitle>Filtros de Reporte</SectionTitle>
          <FiltersCard>
            <FilterRow>
              <div>
                <FilterLabel>Fecha Inicio</FilterLabel>
                <Input type="date" />
              </div>
              <div>
                <FilterLabel>Fecha Fin</FilterLabel>
                <Input type="date" />
              </div>
            </FilterRow>
            <div style={{ marginBottom: '16px' }}>
              <FilterLabel>Médico</FilterLabel>
              <Select>
                <option>Seleccionar Médico</option>
                <option>Dra. Ana López</option>
                <option>Dr. Carlos García</option>
                <option>Dra. Elena García</option>
              </Select>
            </div>
            <ApplyButton>Aplicar Filtros</ApplyButton>
          </FiltersCard>
        </Section>

        <Section>
          <SectionTitle>Métricas Clave</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricHeader>
                <MetricLabel>
                  <Calendar />
                  Citas Totales
                </MetricLabel>
              </MetricHeader>
              <MetricValue>{reportData.totalAppointments.toLocaleString()}</MetricValue>
              <MetricChange>
                <TrendingUp />
                {reportData.totalAppointmentsChange}
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricHeader>
                <MetricLabel>
                  <Users />
                  Pacientes Adultos
                </MetricLabel>
              </MetricHeader>
              <MetricValue>{reportData.adultPatients.toLocaleString()}</MetricValue>
              <MetricChange>
                <TrendingUp />
                {reportData.adultPatientsChange}
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricHeader>
                <MetricLabel>
                  <Users />
                  Pacientes Pediátricos
                </MetricLabel>
              </MetricHeader>
              <MetricValue>{reportData.pediatricPatients.toLocaleString()}</MetricValue>
              <MetricChange>
                <TrendingUp />
                {reportData.pediatricPatientsChange}
              </MetricChange>
            </MetricCard>
          </MetricsGrid>
        </Section>

        <Section>
          <SectionTitle>Citas por Período</SectionTitle>
          <ChartCard>
            <ChartHeader>
              <ChartTitle>Citas por Período</ChartTitle>
              <ViewDetailsButton onClick={() => navigate('/reporte-citas')}>Ver detalles</ViewDetailsButton>
            </ChartHeader>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={reportData.appointmentsByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#33A9FF" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Section>

        <Section>
          <SectionTitle>Ingresos por Servicio</SectionTitle>
          <ChartCard>
            <ChartHeader>
              <ChartTitle>Ingresos por Servicio</ChartTitle>
              <ViewDetailsButton onClick={() => navigate('/reporte-ingresos')}>Ver detalles</ViewDetailsButton>
            </ChartHeader>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={reportData.serviceRevenue}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {reportData.serviceRevenue.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <LegendContainer>
              {reportData.serviceRevenue.map((item, index) => (
                <LegendItem key={index}>
                  <LegendColor color={item.color} />
                  <span>{item.name}</span>
                </LegendItem>
              ))}
            </LegendContainer>
          </ChartCard>
        </Section>

        <Section>
          <SectionTitle>Exportar Reporte</SectionTitle>
          <ExportSection>
            <ExportButtons>
              <ExportButton>
                <FileDown />
                Exportar a PDF
              </ExportButton>
              <ExportButton>
                <FileDown />
                Exportar a CSV
              </ExportButton>
            </ExportButtons>
          </ExportSection>
        </Section>
      </Content>
    </PageContainer>
  );
};

export default Reportes;
